/**
 * El rechazo de Business Central cuando a una tienda le falta la cuenta de pago
 * de una forma de pago.
 *
 * En Business Central, la cuenta contra la que se cobra **no es un dato de la
 * forma de pago sino del par (forma de pago, tienda)**: vive en la tabla
 * `Payment Method Det TPV`, y `CuentaPago` la busca por ese par al armar el
 * apunte del cobro. Si no encuentra la fila, no cae a la cuenta global ni a la
 * contrapartida: lanza `CUENTA_PAGO_NO_CONFIGURADA`.
 *
 * Y ese error sale desde dentro de `OnAfterFinalizePostingOnBeforeCommit`, así
 * que **no se queda sin contabilizar el pago: se deshace el registro entero**.
 * El borrador que el terminal ya había creado en Business Central se queda ahí.
 *
 * **Por qué se reintenta y no sale de la cola.** Esto lo arregla quien configura
 * Business Central añadiendo la fila, sin tocar la caja, y en cuanto exista el
 * mismo documento se registra sin cambiar nada. Es el caso de
 * `SERIE_NO_CONFIGURADA`, no el de `ABONO_EXCEDE`. Reintentar es barato --sólo
 * se repite la llamada de registro, no el alta-- y el guardia del `Id_TPV`
 * impide que el reintento cree una segunda factura.
 *
 * El texto de Business Central termina en «Falta la fila en Payment Method Det
 * TPV», que es para nosotros y no para el cajero: se quita. Lo que sí se
 * conserva es la tienda y la forma de pago, porque es lo que necesita saber
 * quien va a configurarlo.
 */

/** El prefijo estable con el que llega, misma convención que `PAGO_INCOMPLETO`. */
export const PREFIJO_CUENTA_DE_PAGO = "CUENTA_PAGO_NO_CONFIGURADA";

/**
 * Si Business Central rechazó el registro por falta de cuenta de pago.
 *
 * Sin anclar al principio, como `esRechazoPorPagoIncompleto`: este rechazo llega
 * **dentro** del `error.message` del 400 de `Microsoft.NAV.post`, no al empezar
 * la cadena.
 */
export const esRechazoPorCuentaDePagoSinConfigurar = (motivo) =>
  new RegExp(PREFIJO_CUENTA_DE_PAGO).test(String(motivo ?? ""));

/** La frase que nombra la tabla de AL: útil para nosotros, ruido para el cajero. */
const FRASE_INTERNA = /\s*Falta la fila en Payment Method Det TPV\.?/i;

const conMayusculaInicial = (texto) =>
  texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : texto;

/**
 * El motivo para la fila del documento, escrito para quien lo va a leer.
 *
 * @param {string} motivo Lo que dijo Business Central.
 */
export const avisoDeLaCuentaDePagoSinConfigurar = (motivo) => {
  const aviso =
    "Business Central no registró la venta: esta forma de pago no está configurada para tu tienda. " +
    "Avisa a quien configura Business Central; en cuanto la configuren, la venta se manda sola.";

  const detalle = conMayusculaInicial(
    String(motivo ?? "")
      .match(new RegExp(`${PREFIJO_CUENTA_DE_PAGO}:\\s*([\\s\\S]*)`))?.[1]
      ?.replace(FRASE_INTERNA, "")
      .trim() ?? ""
  );

  return detalle ? `${aviso} ${detalle}` : aviso;
};
